import React from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import { Link } from "ds/Button";
import * as Table from "ds/Table";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import getUrl from "Libs/getUrl";
import { canManageEnvironmentVariablesSelector } from "Reducers/environment";
import { canManageProjectVariablesSelector } from "Reducers/project/project";
import VariableLine from "src/organization/pages/project/settings/variables/components/VariableLine/VariableLine";

import * as S from "./VariableSection.styles";

import type { ProjectLevelVariable, Variable } from "@packages/client";

interface VariableSectionProps {
  variables: Array<
    | (Variable & {
        isOverridingFrom?: string | boolean;
      })
    | ProjectLevelVariable
  >;
  type?: "project" | "environment";
  isOverridingFrom?: (variable: Variable) => false | string;
  isInactive?: (variable: Variable) => boolean;
}

const VariableSection = ({
  variables,
  type = "environment",
  isOverridingFrom,
  isInactive
}: VariableSectionProps) => {
  const { environmentId, organizationId, projectId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();
  const intl = useIntl();
  const [sort, setSort] = Table.useSort<{
    variable: string;
    value: string;
  }>({
    key: "variable",
    direction: "asc"
  });

  const sortedVariables = [...variables].sort((a, b) => {
    if (sort.key === "variable") {
      if (a.name < b.name) {
        return sort.direction === "asc" ? -1 : 1;
      }

      if (a.name > b.name) {
        return sort.direction === "asc" ? 1 : -1;
      }

      return 0;
    }

    if (a.value < b.value) {
      return sort.direction === "asc" ? -1 : 1;
    }

    if (a.value > b.value) {
      return sort.direction === "asc" ? 1 : -1;
    }

    return 0;
  });

  const canManageEnvironmentVariables = useSelectorWithUrlParams(
    canManageEnvironmentVariablesSelector
  );
  const canManageProjectVariables = useSelectorWithUrlParams(
    canManageProjectVariablesSelector
  );

  const canManageVariables =
    type === "environment"
      ? canManageEnvironmentVariables
      : canManageProjectVariables;

  const url =
    type === "project"
      ? `/${organizationId}/${projectId}/-/settings/variables/-/new`
      : `/${organizationId}/${projectId}/${encodeURIComponent(
          environmentId
        )}/settings/variables/-/new`;

  const linkDirect = chunks => {
    const link = (
      <Link to={url} disabled={!canManageVariables} analyticId={url}>
        {chunks}
      </Link>
    );
    if (canManageVariables) {
      return link;
    }
    return (
      <Tooltip
        tooltip={intl.formatMessage({
          id: `${type}.variable.access`
        })}
        analyticId={`${type}.variable.access`}
      >
        {link}
      </Tooltip>
    );
  };

  return variables?.length === 0 ? (
    <S.EmptyList>
      <Typography.BodyText weight="semiBold" size="small">
        {intl.formatMessage(
          {
            id: "settings.environment.variables.list.empty"
          },
          {
            link: linkDirect,
            type
          }
        )}
      </Typography.BodyText>
    </S.EmptyList>
  ) : (
    <>
      <Table.Table>
        <S.TableThead>
          <Table.Th
            width="24%"
            onSortChanged={() => {
              setSort("variable");
            }}
            isSortActive={sort.key === "variable"}
            sortDirection={sort.direction}
          >
            <Typography.BodyText weight="semiBold" size="extraSmall">
              {intl.formatMessage({
                id: "settings.project.variables.sort.name"
              })}
            </Typography.BodyText>
          </Table.Th>
          <Table.Th
            width="24%"
            onSortChanged={() => {
              setSort("value");
            }}
            isSortActive={sort.key === "value"}
            sortDirection={sort.direction}
          >
            <Typography.BodyText weight="semiBold" size="extraSmall">
              {intl.formatMessage({
                id: "settings.project.variables.sort.value"
              })}
            </Typography.BodyText>
          </Table.Th>
          <Table.Th width="24%">
            <Typography.BodyText weight="semiBold" size="extraSmall">
              {intl.formatMessage({
                id: "settings.project.variables.sort.type"
              })}
            </Typography.BodyText>
          </Table.Th>
          <S.SpacerTableTh width="24%">&nbsp;</S.SpacerTableTh>
        </S.TableThead>
      </Table.Table>
      {sortedVariables.map(variable => (
        <VariableLine
          key={`var-${type}-${variable.id}`}
          variable={variable}
          url={
            type === "project"
              ? getUrl({
                  key: "organization.project.settings.variables.variable",
                  props: {
                    organizationId,
                    projectId,
                    variableId: variable.id
                  }
                })
              : ""
          }
          isOverridingFrom={
            type === "environment" && isOverridingFrom?.(variable as any)
          }
          isInactive={type === "project" && isInactive?.(variable as any)}
        />
      ))}
    </>
  );
};
export default VariableSection;
